import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import './AdminStaffCreator.css';

// Helper function to generate a UUID if crypto.randomUUID is not available
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback UUID generation for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, 
          v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const AdminStaffCreator = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formValues, setFormValues] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    is_admin: false,
    skip_email: true // Add option to skip email verification
  });

  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // Log environment information
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const isProdEnv = !isLocalhost && window.location.hostname.includes('vercel.app');
        console.log('Environment:', { 
          isLocalhost, 
          isProdEnv,
          hostname: window.location.hostname,
          origin: window.location.origin
        });
        
        if (!session) {
          console.log('No active session found - not admin');
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        
        console.log('Checking admin status for user:', session.user.id);
        
        // For localhost/development, auto-grant admin access
        if (isLocalhost) {
          console.log('Development environment detected - auto-granting admin access');
          setIsAdmin(true);
          setLoading(false);
          return;
        }
        
        // Try using an RPC function first (which can bypass RLS)
        try {
          const { data: rpcData, error: rpcError } = await supabase
            .rpc('check_admin_status', { user_id: session.user.id });
            
          if (rpcError) {
            console.error('RPC admin check failed:', rpcError);
            
            // Fallback to using execute_sql RPC
            try {
              const { data: sqlData, error: sqlError } = await supabase.rpc('execute_sql', { 
                sql_statement: `SELECT is_admin, user_type FROM app_users WHERE id = '${session.user.id}'`
              });
              
              if (sqlError) {
                console.error('SQL execution error:', sqlError);
                setIsAdmin(false);
              } else {
                console.log('Admin check SQL result:', sqlData);
                
                // Check if user is admin or staff
                const hasAdminPrivileges = sqlData && sqlData[0] && 
                  (sqlData[0].is_admin === true || sqlData[0].user_type === 'staff');
                  
                setIsAdmin(hasAdminPrivileges);
              }
            } catch (sqlError) {
              console.error('SQL check error:', sqlError);
              setIsAdmin(false);
            }
          } else {
            // RPC success - check if admin
            console.log('Admin check RPC result:', rpcData);
            setIsAdmin(rpcData === true);
          }
        } catch (error) {
          console.error('Admin check error:', error);
          setIsAdmin(false);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Validate form data
      if (!formValues.email || !formValues.password || !formValues.first_name || !formValues.last_name) {
        throw new Error('All fields are required');
      }

      console.log('Creating new staff user:', formValues.email);

      // Try with admin API first, which can skip email verification
      if (formValues.skip_email) {
        console.log("Attempting to create pre-confirmed user with admin API");
        try {
          let { data: adminUserData, error: adminUserError } = await supabase.auth.admin.createUser({
            email: formValues.email,
            password: formValues.password,
            email_confirm: true, // Pre-confirm the email so no verification email is sent
            user_metadata: {
              first_name: formValues.first_name,
              last_name: formValues.last_name,
              is_admin: formValues.is_admin,
              user_type: 'staff'
            }
          });
          
          if (adminUserError) {
            console.warn("Admin API error:", adminUserError.message);
            throw adminUserError;
          }
          
          // Continue with admin user data
          console.log("User successfully created with admin API");
          await processCreatedUser(adminUserData);
          return;
        } catch (adminError) {
          console.warn("Admin API failed, will fall back to regular signup:", adminError.message);
          // Continue to fallback method
        }
      }
      
      // Fallback to regular signup (if admin API fails or skip_email is false)
      console.log("Creating user with standard signup flow");
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: formValues.email,
        password: formValues.password,
        options: {
          data: {
            first_name: formValues.first_name,
            last_name: formValues.last_name,
            is_admin: formValues.is_admin,
            user_type: 'staff'
          },
          emailRedirectTo: `${window.location.origin}/login`
        }
      });
      
      if (signUpError) {
        if (signUpError.message.includes("rate limit")) {
          throw new Error(`Email rate limit exceeded. Please wait a few minutes before creating more users, or try with "Skip Email Verification" checked.`);
        }
        throw new Error(`Auth error: ${signUpError.message}`);
      }
      
      if (!signUpData?.user) {
        throw new Error('Failed to create user account');
      }
      
      await processCreatedUser(signUpData);
      
    } catch (error) {
      console.error('Error creating staff user:', error);
      setErrorMessage(error.message);
    } finally {
      setCreating(false);
    }
  };

  // Helper function to process a created user
  const processCreatedUser = async (userData) => {
    // Get the user ID
    const userId = userData?.user?.id;
    
    if (!userId) {
      throw new Error('Failed to retrieve user ID');
    }
    
    console.log("User created with ID:", userId);

    try {
      // Use RPC function to bypass RLS policies
      console.log("Calling create_staff_user RPC function with is_admin:", formValues.is_admin);
      const { data: rpcData, error: rpcError } = await supabase.rpc('create_staff_user', {
        user_id: userId,
        user_email: formValues.email,
        user_first_name: formValues.first_name,
        user_last_name: formValues.last_name,
        user_is_admin: Boolean(formValues.is_admin) // Ensure it's explicitly a boolean
      });
      
      if (rpcError) {
        console.error("RPC function error:", rpcError);
        
        // Fallback if RPC doesn't exist yet: try direct SQL via function
        console.log("Falling back to direct app_users operations");
        
        // First, check if an app_users entry already exists
        const { data: existingUser } = await supabase
          .from('app_users')
          .select('*')
          .eq('id', userId)
          .single();
        
        if (existingUser) {
          console.log("Existing app_users entry found, updating it");
          
          // Update the existing record - using direct SQL as fallback for RLS issues
          const booleanValue = formValues.is_admin ? 'TRUE' : 'FALSE'; // Properly format for SQL
          const { data: sqlData, error: sqlError } = await supabase.rpc('execute_sql', { 
            sql_statement: `UPDATE app_users 
                           SET first_name = '${formValues.first_name}',
                               last_name = '${formValues.last_name}',
                               user_type = 'staff',
                               is_admin = ${booleanValue}
                           WHERE id = '${userId}'` 
          });
          
          if (sqlError) {
            console.error("SQL execution error:", sqlError);
            
            // Last resort: try regular update
            const { error: updateError } = await supabase
              .from('app_users')
              .update({
                first_name: formValues.first_name,
                last_name: formValues.last_name,
                user_type: 'staff',
                is_admin: formValues.is_admin
              })
              .eq('id', userId);
              
            if (updateError) {
              throw new Error(`Database error: ${updateError.message}`);
            }
          }
        } else {
          console.log("No existing app_users entry, creating new one");
          
          // Try direct SQL insert as fallback
          const booleanValue = formValues.is_admin ? 'TRUE' : 'FALSE'; // Properly format for SQL
          const { data: sqlData, error: sqlError } = await supabase.rpc('execute_sql', { 
            sql_statement: `INSERT INTO app_users (id, email, first_name, last_name, user_type, is_admin)
                           VALUES ('${userId}', '${formValues.email}', 
                                   '${formValues.first_name}', '${formValues.last_name}',
                                   'staff', ${booleanValue})` 
          });
          
          if (sqlError) {
            console.error("SQL execution error:", sqlError);
            
            // Last resort: try regular insert
            const { error: insertError } = await supabase
              .from('app_users')
              .insert({
                id: userId,
                email: formValues.email,
                first_name: formValues.first_name,
                last_name: formValues.last_name,
                user_type: 'staff',
                is_admin: formValues.is_admin
              });
              
            if (insertError) {
              throw new Error(`Database error: ${insertError.message}`);
            }
          }
        }
      } else {
        console.log("Successfully used RPC function to create/update app_users record");
      }
      
      const adminText = formValues.is_admin ? 'admin' : 'staff';
      const verificationText = formValues.skip_email 
        ? "The account is already confirmed and ready to use." 
        : "User will receive a confirmation email and will be able to log in after confirming their email.";
      
      setSuccessMessage(`${formValues.first_name} ${formValues.last_name} (${formValues.email}) created successfully as ${adminText} user! ${verificationText}`);
      
      // Reset form
      setFormValues({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        is_admin: false,
        skip_email: true
      });
      
    } catch (error) {
      console.error("Error in processCreatedUser:", error);
      throw new Error(`Failed to create app_users record: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="not-admin">
        <h2>Access Denied</h2>
        <p>You do not have administrator privileges to create staff accounts.</p>
        <Link to="/admin" className="admin-link">
          Back to Admin Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="admin-staff-creator">
      <div className="creator-header">
        <h2>Create Staff/Admin User</h2>
        <Link to="/admin" className="back-link">
          &larr; Back to Admin Dashboard
        </Link>
      </div>

      <div className="creator-content">
        <div className="creator-info">
          <h3>Staff User Creation</h3>
          <p>
            This form creates authenticated users with staff or admin privileges. The process happens in two steps:
          </p>
          <ol>
            <li>Create a user in Supabase Auth</li>
            <li>Create or update an entry in the app_users table with staff/admin privileges</li>
          </ol>
          <p>
            By default, email verification is skipped to avoid rate limiting. If you uncheck this option, the user will receive a confirmation email and will need to confirm before logging in.
          </p>
          {errorMessage && errorMessage.includes("rate limit") && (
            <div className="warning-box">
              <p><strong>Rate Limit Warning:</strong> Supabase limits how many verification emails can be sent in a short time period. If you're seeing rate limit errors, use the "Skip Email Verification" option or wait 10-15 minutes before creating more users.</p>
            </div>
          )}
          {errorMessage && errorMessage.includes("infinite recursion") && (
            <div className="warning-box">
              <p><strong>Database Policy Error:</strong> Your app_users table has Row Level Security policies that are causing recursion. You'll need to create a PostgreSQL function to bypass these policies. See instructions below:</p>
              <pre style={{background: '#f5f5f5', padding: '10px', overflow: 'auto'}}>
{`-- Run this SQL in the Supabase SQL Editor:

CREATE OR REPLACE FUNCTION create_staff_user(
  user_id UUID,
  user_email TEXT,
  user_first_name TEXT,
  user_last_name TEXT,
  user_is_admin BOOLEAN
) RETURNS BOOLEAN
LANGUAGE plpgsql
SECURITY DEFINER -- This runs with the privileges of the function creator
AS $$
BEGIN
  -- Check if user exists
  IF EXISTS (SELECT 1 FROM app_users WHERE id = user_id) THEN
    -- Update existing user
    UPDATE app_users
    SET first_name = user_first_name,
        last_name = user_last_name,
        user_type = 'staff',
        is_admin = user_is_admin
    WHERE id = user_id;
  ELSE
    -- Insert new user
    INSERT INTO app_users (id, email, first_name, last_name, user_type, is_admin)
    VALUES (user_id, user_email, user_first_name, user_last_name, 'staff', user_is_admin);
  END IF;
  
  RETURN TRUE;
END;
$$;`}
              </pre>
            </div>
          )}
        </div>

        <div className="creator-form-container">
          {successMessage && (
            <div className="success-message">
              <p>{successMessage}</p>
            </div>
          )}

          {errorMessage && (
            <div className="error-message">
              <p>{errorMessage}</p>
            </div>
          )}

          <form className="creator-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formValues.email}
                onChange={handleInputChange}
                required
                disabled={creating}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formValues.password}
                onChange={handleInputChange}
                required
                disabled={creating}
                minLength="8"
              />
              <small>Minimum 8 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="first_name">First Name</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formValues.first_name}
                onChange={handleInputChange}
                required
                disabled={creating}
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formValues.last_name}
                onChange={handleInputChange}
                required
                disabled={creating}
              />
            </div>

            <div className="form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="is_admin"
                  checked={formValues.is_admin}
                  onChange={handleInputChange}
                  disabled={creating}
                />
                Give Admin Privileges
              </label>
              <small>Enables access to all admin features</small>
            </div>
            
            <div className="form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="skip_email"
                  checked={formValues.skip_email}
                  onChange={handleInputChange}
                  disabled={creating}
                />
                Skip Email Verification
              </label>
              <small>Creates pre-confirmed accounts (recommended to avoid rate limits)</small>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="create-button"
                disabled={creating}
              >
                {creating ? (
                  <>
                    <span className="spinner-small"></span>
                    Creating...
                  </>
                ) : (
                  'Create Staff User'
                )}
              </button>
              <Link to="/admin" className="cancel-button">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminStaffCreator; 